import { useRef, useState, type RefObject } from "react";

import { MarkdownContent } from "~/components/rich-content/MarkdownContent";
import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { ProjectContentReadabilityControls } from "~/components/rich-content/ProjectContentReadabilityControls";

export interface ProjectDescriptionEditorProps {
  readonly projectId: string;
  readonly description: string | null;
  /** When set, clamp the read view to N lines with a Show more control. */
  readonly previewLines?: number;
  readonly onSave: (description: string) => Promise<void>;
}

function descriptionClampClass(
  previewLines: number | undefined,
  expanded: boolean,
): string | undefined {
  if (previewLines == null || expanded) return undefined;
  if (previewLines <= 2) return "line-clamp-2";
  if (previewLines <= 3) return "line-clamp-3";
  return "line-clamp-4";
}

function DescriptionEditorForm(props: {
  readonly projectId: string;
  readonly draft: string;
  readonly saving: boolean;
  readonly error: string | null;
  readonly onDraftChange: (value: string) => void;
  readonly onCommit: () => void;
  readonly onCancel: () => void;
}) {
  return (
    <div
      onKeyDownCapture={(event) => {
        if (event.key !== "Escape" || props.saving) return;
        event.preventDefault();
        event.stopPropagation();
        props.onCancel();
      }}
    >
      <MarkdownEditor
        value={props.draft}
        onChange={props.onDraftChange}
        autoFocus
        ariaLabel="Project description"
        placeholder="Describe this project…"
      />
      <ProjectContentReadabilityControls
        projectId={props.projectId}
        surface="description"
        value={props.draft}
        disabled={props.saving}
        onChange={props.onDraftChange}
      />
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={props.onCommit}
          disabled={props.saving}
          className="inline-flex h-7 items-center rounded-md bg-foreground px-2.5 text-xs font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {props.saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={props.onCancel}
          className="inline-flex h-7 items-center rounded-md px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          Cancel
        </button>
      </div>
      {props.error ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

function DescriptionReadView(props: {
  readonly description: string;
  readonly previewLines?: number;
  readonly editButtonRef: RefObject<HTMLButtonElement | null>;
  readonly onEdit: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const clampClass = descriptionClampClass(props.previewLines, expanded);
  const canClamp = props.previewLines != null && props.description.trim().length > 160;

  return (
    <div className="group relative -mx-2 rounded-md px-2 py-1.5 transition-colors hover:bg-muted">
      <div className={clampClass}>
        <MarkdownContent variant="compact" className="pr-12">
          {props.description}
        </MarkdownContent>
      </div>
      <button
        ref={props.editButtonRef}
        type="button"
        onClick={props.onEdit}
        className="absolute right-2 top-1.5 rounded px-1.5 py-1 text-xs font-medium text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
      >
        Edit
      </button>
      {canClamp ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
}

export function ProjectDescriptionEditor({
  projectId,
  description,
  previewLines,
  onSave,
}: ProjectDescriptionEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  const finishEditing = () => {
    setEditing(false);
    requestAnimationFrame(() => editButtonRef.current?.focus());
  };

  const startEdit = () => {
    setDraft(description ?? "");
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(description ?? "");
    setError(null);
    finishEditing();
  };

  const commit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      finishEditing();
    } catch {
      setError("Couldn’t save the description. Your draft is still here—try again.");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <DescriptionEditorForm
        projectId={projectId}
        draft={draft}
        saving={saving}
        error={error}
        onDraftChange={setDraft}
        onCommit={() => void commit()}
        onCancel={cancelEdit}
      />
    );
  }

  if (!description) {
    return (
      <button
        ref={editButtonRef}
        type="button"
        onClick={startEdit}
        className="-mx-2 block w-full rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground/60 transition-colors hover:bg-muted hover:text-muted-foreground"
      >
        Add a description…
      </button>
    );
  }

  return (
    <DescriptionReadView
      description={description}
      {...(previewLines !== undefined ? { previewLines } : {})}
      editButtonRef={editButtonRef}
      onEdit={startEdit}
    />
  );
}
